// Runtime-generated sprite atlas for billboard particles and ground decals.
//
// Follows the existing makeGroundTexture idiom: everything is drawn to a canvas at boot, so the
// game gains textured smoke, embers, and scorch marks without shipping a single binary asset.
//
// One 4x4 atlas serves both systems. Cells are addressed by index (see SPRITE), and the shaders
// derive UVs from `cell` so a single draw call can mix sprite kinds.

import * as THREE from 'three';

export const ATLAS_COLS = 4;
export const ATLAS_ROWS = 4;
const CELL = 256;

/** Cell indices, row-major from the top-left. */
export const SPRITE = {
  /** soft round puff — smoke, dust */
  puff: 0,
  /** bright core with a fast falloff — sparks, embers, muzzle flash */
  ember: 1,
  /** elongated streak — projectile trails, speed lines */
  streak: 2,
  /** thin expanding ring — shockwaves, impact rings */
  ring: 3,
  /** ragged flame lick */
  flame: 4,
  /** four-point star flare */
  flare: 5,
  /** irregular blast scorch — decal */
  scorch: 6,
  /** spatter — decal */
  spatter: 7,
  /** cracked crater — decal */
  crater: 8,
  /** soft shadow/stain oval — decal */
  stain: 9,
} as const;

function cellOrigin(index: number): { x: number; y: number } {
  return { x: (index % ATLAS_COLS) * CELL, y: Math.floor(index / ATLAS_COLS) * CELL };
}

/**
 * Deterministic PRNG. The render layer is allowed Math.random, but a fixed atlas means the
 * smoke and scorch marks look the same every session, which matters for bug reports.
 */
function makeRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function drawPuff(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  // Several overlapping soft lobes so the silhouette is cloudy rather than a perfect disc.
  for (let i = 0; i < 7; i++) {
    const angle = rng() * Math.PI * 2;
    const distance = rng() * CELL * 0.16;
    const radius = CELL * (0.2 + rng() * 0.16);
    const x = ox + c + Math.cos(angle) * distance;
    const y = oy + c + Math.sin(angle) * distance;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, 'rgba(255,255,255,0.34)');
    gradient.addColorStop(0.5, 'rgba(255,255,255,0.14)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
}

function drawEmber(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  const c = CELL / 2;
  const gradient = ctx.createRadialGradient(ox + c, oy + c, 0, ox + c, oy + c, CELL * 0.42);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.18, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.22)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(ox, oy, CELL, CELL);
}

function drawStreak(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  const c = CELL / 2;
  // Vertical streak; the shader rotates instances to align with velocity.
  const gradient = ctx.createLinearGradient(ox, oy, ox, oy + CELL);
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.42, 'rgba(255,255,255,0.9)');
  gradient.addColorStop(0.55, 'rgba(255,255,255,1)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.beginPath();
  // Lens shape: widest at the middle, tapering to points.
  ctx.moveTo(ox + c, oy);
  ctx.quadraticCurveTo(ox + c + CELL * 0.11, oy + c, ox + c, oy + CELL);
  ctx.quadraticCurveTo(ox + c - CELL * 0.11, oy + c, ox + c, oy);
  ctx.clip();
  ctx.fillStyle = gradient;
  ctx.fillRect(ox, oy, CELL, CELL);
  ctx.restore();
}

function drawRing(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  const c = CELL / 2;
  const gradient = ctx.createRadialGradient(ox + c, oy + c, CELL * 0.3, ox + c, oy + c, CELL * 0.48);
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.55, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(0.78, 'rgba(255,255,255,0.5)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(ox, oy, CELL, CELL);
}

function drawFlame(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(ox + c, oy + CELL * 0.06);
  // Ragged sides so it reads as fire rather than a teardrop.
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const width = Math.sin(t * Math.PI) * CELL * 0.3 * (0.72 + rng() * 0.5);
    ctx.lineTo(ox + c + width, oy + CELL * 0.06 + t * CELL * 0.88);
  }
  for (let i = 10; i >= 0; i--) {
    const t = i / 10;
    const width = Math.sin(t * Math.PI) * CELL * 0.3 * (0.72 + rng() * 0.5);
    ctx.lineTo(ox + c - width, oy + CELL * 0.06 + t * CELL * 0.88);
  }
  ctx.closePath();
  ctx.clip();
  const gradient = ctx.createLinearGradient(ox, oy + CELL, ox, oy);
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.35, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(0.75, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(ox, oy, CELL, CELL);
  ctx.restore();
}

function drawFlare(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  const c = CELL / 2;
  drawEmber(ctx, ox, oy);
  // Four-point star spikes.
  ctx.save();
  ctx.translate(ox + c, oy + c);
  for (let i = 0; i < 4; i++) {
    ctx.rotate(Math.PI / 2);
    const gradient = ctx.createLinearGradient(0, 0, 0, -CELL * 0.48);
    gradient.addColorStop(0, 'rgba(255,255,255,0.75)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(-CELL * 0.022, 0);
    ctx.lineTo(0, -CELL * 0.48);
    ctx.lineTo(CELL * 0.022, 0);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function drawScorch(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  // Irregular blast: a dark core with radiating soot fingers.
  const core = ctx.createRadialGradient(ox + c, oy + c, 0, ox + c, oy + c, CELL * 0.4);
  core.addColorStop(0, 'rgba(255,255,255,0.92)');
  core.addColorStop(0.5, 'rgba(255,255,255,0.55)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = core;
  ctx.fillRect(ox, oy, CELL, CELL);
  for (let i = 0; i < 22; i++) {
    const angle = rng() * Math.PI * 2;
    const length = CELL * (0.22 + rng() * 0.24);
    const width = CELL * (0.02 + rng() * 0.05);
    ctx.save();
    ctx.translate(ox + c, oy + c);
    ctx.rotate(angle);
    const gradient = ctx.createLinearGradient(0, 0, length, 0);
    gradient.addColorStop(0, 'rgba(255,255,255,0.5)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, -width / 2, length, width);
    ctx.restore();
  }
}

function drawSpatter(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  for (let i = 0; i < 30; i++) {
    const angle = rng() * Math.PI * 2;
    const distance = Math.pow(rng(), 0.6) * CELL * 0.42;
    const radius = CELL * (0.012 + rng() * 0.055) * (1 - distance / (CELL * 0.6));
    if (radius <= 0) continue;
    ctx.fillStyle = `rgba(255,255,255,${0.35 + rng() * 0.5})`;
    ctx.beginPath();
    ctx.ellipse(ox + c + Math.cos(angle) * distance, oy + c + Math.sin(angle) * distance, radius, radius * (0.6 + rng() * 0.7), angle, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCrater(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  const rim = ctx.createRadialGradient(ox + c, oy + c, CELL * 0.18, ox + c, oy + c, CELL * 0.44);
  rim.addColorStop(0, 'rgba(255,255,255,0.75)');
  rim.addColorStop(0.7, 'rgba(255,255,255,0.4)');
  rim.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = rim;
  ctx.fillRect(ox, oy, CELL, CELL);
  // Radial cracks.
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  for (let i = 0; i < 9; i++) {
    const angle = (i / 9) * Math.PI * 2 + rng() * 0.4;
    ctx.lineWidth = 1 + rng() * 2.5;
    ctx.beginPath();
    ctx.moveTo(ox + c, oy + c);
    let x = ox + c;
    let y = oy + c;
    let a = angle;
    for (let segment = 0; segment < 4; segment++) {
      a += (rng() - 0.5) * 0.6;
      const step = CELL * (0.06 + rng() * 0.07);
      x += Math.cos(a) * step;
      y += Math.sin(a) * step;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function drawStain(ctx: CanvasRenderingContext2D, ox: number, oy: number, rng: () => number): void {
  const c = CELL / 2;
  for (let i = 0; i < 5; i++) {
    const angle = rng() * Math.PI * 2;
    const distance = rng() * CELL * 0.1;
    const rx = CELL * (0.24 + rng() * 0.14);
    const ry = rx * (0.7 + rng() * 0.5);
    const x = ox + c + Math.cos(angle) * distance;
    const y = oy + c + Math.sin(angle) * distance;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, Math.max(rx, ry));
    gradient.addColorStop(0, 'rgba(255,255,255,0.4)');
    gradient.addColorStop(0.6, 'rgba(255,255,255,0.2)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.scale(1, ry / rx);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, rx, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

let cached: THREE.CanvasTexture | null = null;

/**
 * The shared sprite atlas. Luminance-only (white on transparent) so instances can be tinted
 * freely by per-instance colour — one atlas serves every faction palette.
 */
export function getSpriteAtlas(): THREE.CanvasTexture {
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * CELL;
  canvas.height = ATLAS_ROWS * CELL;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRng(0x5161f0);

  const draw: Record<number, () => void> = {
    [SPRITE.puff]: () => drawPuff(ctx, cellOrigin(SPRITE.puff).x, cellOrigin(SPRITE.puff).y, rng),
    [SPRITE.ember]: () => drawEmber(ctx, cellOrigin(SPRITE.ember).x, cellOrigin(SPRITE.ember).y),
    [SPRITE.streak]: () => drawStreak(ctx, cellOrigin(SPRITE.streak).x, cellOrigin(SPRITE.streak).y),
    [SPRITE.ring]: () => drawRing(ctx, cellOrigin(SPRITE.ring).x, cellOrigin(SPRITE.ring).y),
    [SPRITE.flame]: () => drawFlame(ctx, cellOrigin(SPRITE.flame).x, cellOrigin(SPRITE.flame).y, rng),
    [SPRITE.flare]: () => drawFlare(ctx, cellOrigin(SPRITE.flare).x, cellOrigin(SPRITE.flare).y),
    [SPRITE.scorch]: () => drawScorch(ctx, cellOrigin(SPRITE.scorch).x, cellOrigin(SPRITE.scorch).y, rng),
    [SPRITE.spatter]: () => drawSpatter(ctx, cellOrigin(SPRITE.spatter).x, cellOrigin(SPRITE.spatter).y, rng),
    [SPRITE.crater]: () => drawCrater(ctx, cellOrigin(SPRITE.crater).x, cellOrigin(SPRITE.crater).y, rng),
    [SPRITE.stain]: () => drawStain(ctx, cellOrigin(SPRITE.stain).x, cellOrigin(SPRITE.stain).y, rng),
  };
  for (const key of Object.keys(draw)) draw[Number(key)]();

  cached = new THREE.CanvasTexture(canvas);
  // Luminance mask, not colour — must stay linear so tinting is predictable.
  cached.colorSpace = THREE.NoColorSpace;
  // Clamp: bilinear filtering at a cell edge would otherwise bleed in the neighbouring sprite.
  cached.wrapS = THREE.ClampToEdgeWrapping;
  cached.wrapT = THREE.ClampToEdgeWrapping;
  cached.generateMipmaps = true;
  cached.minFilter = THREE.LinearMipmapLinearFilter;
  return cached;
}
